import React, {useState} from "react"
import {Col, Row} from "antd"
import {useMutation, useQueryClient} from "@tanstack/react-query"
import {useTranslation} from "react-i18next"
import {BaseButton, BasePopup} from "components"
import {FormLabel} from "components/Form"
import CampusSelect from "components/CampusSelect"
import ProgramSelect from "components/ProgramSelect"
import {NewStudentStatusSelect} from "components/NewStudentStatusSelect"
import {handleError, toastSuccess} from "helpers"
import {sapCheckpointsService} from "services"
import {RunSapCheckpointChecksPayload} from "types/settings/sap-checkpoint-check"
import styles from "./RunSapChecksPopup.module.css"

type Props = {
    isVisible: boolean
    onClose: () => void
}

const RunSapChecksPopup: React.FC<Props> = ({isVisible, onClose}) => {
    const {t} = useTranslation(["academics", "common"])
    const queryClient = useQueryClient()
    const [campuses, setCampuses] = useState<any[]>([])
    const [programs, setPrograms] = useState<any[]>([])
    const [studentStatuses, setStudentStatuses] = useState<any[]>([])

    const runSapMutation = useMutation(
        (params: RunSapCheckpointChecksPayload) => sapCheckpointsService.runChecks(params),
        {
            onSuccess: (result) => {
                toastSuccess(t("academics.sapChecks.runSapSuccess", {count: result.created ?? 0}))
                queryClient.invalidateQueries({queryKey: ["sap-checkpoint-checks-all"]})
                queryClient.invalidateQueries({queryKey: ["sap-checkpoint-checks-stats"]})
                onClose()
            },
            onError: (error) => handleError(error)
        }
    )

    const handleRunSAP = () => {
        runSapMutation.mutate({
            campusIds: campuses.length > 0 ? campuses.map((item) => item.id) : undefined,
            programIds: programs.length > 0 ? programs.map((item) => item.id) : undefined,
            studentStatusIds: studentStatuses.length > 0 ? studentStatuses.map((item) => item.statusId) : undefined
        })
    }

    return (
        <BasePopup
            isShow={isVisible}
            onClose={onClose}
            leftIcon="FILE_TEXT"
            leftIconColor="#fff"
            dialogContentClassName={styles.popupContent}
            destroyOnClose
            keyboard={false}
            maskClosable={false}
            width="auto">
            <div className={styles.container}>
                <h2 className={styles.title}>{t("academics.sapChecks.runSap")}</h2>
                <p className={styles.description}>{t("academics.sapChecks.runSapDescription")}</p>

                <div className={styles.filters}>
                    <Row gutter={[24, 24]}>
                        <Col span={24}>
                            <FormLabel label={t("academics.sapChecks.campus")} />
                            <CampusSelect
                                value={campuses}
                                onChange={(values) => setCampuses(values)}
                                isMulti
                                isClearable
                                placeholder={t("academics.sapChecks.selectCampuses")}
                            />
                        </Col>
                        <Col span={24}>
                            <FormLabel label={t("academics.sapChecks.program")} />
                            <ProgramSelect
                                value={programs}
                                onChange={(values) => setPrograms(values)}
                                isMulti
                                isClearable
                                placeholder={t("academics.sapChecks.selectPrograms")}
                            />
                        </Col>
                        <Col span={24}>
                            <FormLabel label={t("academics.sapChecks.filters.studentStatus")} />
                            <NewStudentStatusSelect
                                value={studentStatuses}
                                onChange={(values) => setStudentStatuses(values)}
                                isMulti
                                isClearable
                                placeholder={t("academics.sapChecks.selectStudentStatuses")}
                            />
                        </Col>
                    </Row>
                </div>

                <div className={styles.actions}>
                    <BaseButton
                        title={t("common:action.cancel")}
                        onClick={onClose}
                        variant="secondary"
                        disabled={runSapMutation.isLoading}
                    />
                    <BaseButton
                        title={t("academics.sapChecks.runSap")}
                        onClick={handleRunSAP}
                        loading={runSapMutation.isLoading}
                    />
                </div>
            </div>
        </BasePopup>
    )
}

export default RunSapChecksPopup
